import React from 'react';
import Skeleton from 'components/LoadingSkeleton';

interface TableSkeletonProps {
  /** Number of rows to display */
  rows?: number;
  /** Layout type - 'grid' for TodayLessons style, 'table' for CustomTable style */
  layout?: 'grid' | 'table';
  /** Custom className for the container */
  className?: string;
}

const TableSkeleton = ({ rows = 3, layout = 'table', className = '' }: TableSkeletonProps) => {
  const rowArray = Array.from({ length: rows }, (_, index) => index);

  if (layout === 'grid') {
    // Grid layout for TodayLessons
    return (
      <div className={`space-y-3 ${className}`}>
        {rowArray.map((index) => (
          <div
            key={index}
            className="h-16 rounded-xl border border-color-border-card-lightmode dark:border-color-border-card-darkmode"
          >
            <div className="grid h-full grid-cols-[auto_auto_1fr_auto_auto_1fr_auto] items-center gap-2 px-3 lg:grid-cols-[120px_100px_280px_260px_80px_1fr_auto] lg:gap-4">
              {/* Time */}
              <div className="flex items-center justify-start lg:justify-center">
                <Skeleton width={60} height={16} />
              </div>

              {/* Duration */}
              <div className="flex items-center">
                <Skeleton width={50} height={14} />
              </div>

              {/* Type */}
              <div className="flex min-w-0 items-center">
                <Skeleton width={120} height={16} />
              </div>

              {/* Location - Hidden on mobile, shown on lg+ */}
              <div className="hidden items-center lg:flex">
                <Skeleton width={160} height={14} />
              </div>

              {/* Capacity */}
              <div className="flex items-center justify-center">
                <Skeleton width={40} height={14} />
              </div>

              {/* Client(s) */}
              <div className="flex min-w-0 items-center">
                <div className="flex items-center gap-2">
                  <Skeleton circle width={32} height={32} />
                  <Skeleton width={80} height={14} className="hidden sm:block" />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end">
                <div className="flex items-center gap-1 lg:gap-2.5">
                  <Skeleton width={60} height={32} />
                  <Skeleton width={50} height={32} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Table layout for CustomTable components
  return (
    <div
      className={`rounded-xl border border-color-border-card-lightmode dark:border-color-border-card-darkmode ${className}`}
    >
      {/* Table header */}
      <div className="border-b border-color-border-card-lightmode p-4 dark:border-color-border-card-darkmode">
        <div className="grid grid-cols-5 gap-4">
          <Skeleton width={80} height={14} />
          <Skeleton width={100} height={14} />
          <Skeleton width={120} height={14} />
          <Skeleton width={80} height={14} />
          <Skeleton width={60} height={14} />
        </div>
      </div>

      {/* Table rows */}
      <div className="divide-y divide-color-border-card-lightmode dark:divide-color-border-card-darkmode">
        {rowArray.map((index) => (
          <div key={index} className="p-4">
            <div className="grid grid-cols-5 items-center gap-4">
              {/* First column - usually has avatar/icon */}
              <div className="flex items-center gap-3">
                <Skeleton circle width={32} height={32} />
                <div className="space-y-1">
                  <Skeleton width={80} height={14} />
                  <Skeleton width={60} height={12} />
                </div>
              </div>

              {/* Second column */}
              <div>
                <Skeleton width={90} height={14} />
              </div>

              {/* Third column */}
              <div>
                <Skeleton width={110} height={14} />
              </div>

              {/* Fourth column */}
              <div className="flex items-center gap-1">
                <Skeleton circle width={8} height={8} />
                <Skeleton width={70} height={14} />
              </div>

              {/* Fifth column - actions */}
              <div className="flex items-center gap-2">
                <Skeleton width={70} height={28} />
                <Skeleton width={50} height={28} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TableSkeleton;
